import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Cookies from 'js-cookie';
import React, { FormEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useLoginUserMutation } from '../../services/api/userService';
import { captureError } from '../../services/monitoring/sentry';
import { userLoggedIn } from '../../state/reducers/root';
import theme from '../../theme/theme';
import { isEmailValid } from '../../utils/validation';
import { PrimaryButton } from '../base/Buttons';
import FinchLogo from '../base/FinchLogo';

interface LoginFormState {
  email: string;
  password: string;
}

const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);

const handleGoogleLogin = async () => {
  const backendHost = import.meta.env.VITE_APP_DJANGO_BACKEND_HOST;
  const callbackUrl = `${window.location.origin}/auth/callback`;

  // Ensure the CSRF cookie is set by touching the backend first.
  // This handles the case where the cookie hasn't been set yet
  // (e.g., check-session failed or backend was restarting).
  if (!Cookies.get('csrftoken')) {
    try {
      const response = await fetch(
        `${backendHost}/_allauth/browser/v1/config`,
        {
          credentials: 'include',
        },
      );
      if (!response.ok) {
        console.error('Failed to fetch CSRF token');
      }
    } catch (err) {
      console.error('Error fetching CSRF token:', err);
    }
  }

  const csrfToken = Cookies.get('csrftoken');
  if (!csrfToken) {
    console.error('CSRF token not available');
    return;
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${backendHost}/_allauth/browser/v1/auth/provider/redirect`;
  const csrfInput = document.createElement('input');
  csrfInput.type = 'hidden';
  csrfInput.name = 'csrfmiddlewaretoken';
  csrfInput.value = csrfToken;
  form.appendChild(csrfInput);

  const providerInput = document.createElement('input');
  providerInput.type = 'hidden';
  providerInput.name = 'provider';
  providerInput.value = 'google';
  form.appendChild(providerInput);

  const callbackInput = document.createElement('input');
  callbackInput.type = 'hidden';
  callbackInput.name = 'callback_url';
  callbackInput.value = callbackUrl;
  form.appendChild(callbackInput);

  const processInput = document.createElement('input');
  processInput.type = 'hidden';
  processInput.name = 'process';
  processInput.value = 'login';
  form.appendChild(processInput);

  document.body.appendChild(form);
  form.submit();
};

const LoginForm: React.FC = () => {
  const [formState, setFormState] = useState<LoginFormState>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isConnectionError, setIsConnectionError] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [loginUser] = useLoginUserMutation();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isEmailValid(formState.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await loginUser({
        username: formState.email,
        password: formState.password,
        auth_secret: searchParams.get('auth_secret') || undefined,
      }).unwrap();

      dispatch(userLoggedIn(response.user));
    } catch (e) {
      captureError(e, { tags: { feature: 'login' } });
      const err = e as { status?: number | string; data?: { code?: string } };
      const status = err?.status;
      const connectionError =
        status === 404 ||
        status === 'FETCH_ERROR' ||
        status === undefined ||
        status === 0;
      const isAccountDisabled =
        err?.data?.code === 'account_disabled';
      const isInvalidCredentials =
        err?.data?.code === 'invalid_credentials' ||
        err?.data?.code === 'validation_error' ||
        status === 401;
      setIsConnectionError(connectionError);
      setError(
        connectionError
          ? 'Unable to connect to Finch. Please make sure you are connected to Tailscale and try again.'
          : isAccountDisabled
            ? 'Your account has been disabled. Please contact your administrator.'
            : isInvalidCredentials
              ? 'Invalid email or password. Please try again.'
              : 'Something went wrong. Please clear your cookies and try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    (prop: keyof LoginFormState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState({ ...formState, [prop]: event.target.value });
    };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <FinchLogo width="160px" />
        {error && isConnectionError && (
          <Alert
            severity="warning"
            sx={{ mt: '36px', width: '100%' }}
          >
            <AlertTitle>Unable to connect to Finch</AlertTitle>
            Please make sure you are connected to{' '}
            <Link
              href="https://login.tailscale.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 600 }}
            >
              Tailscale
            </Link>{' '}
            and try again.
          </Alert>
        )}
        {error && !isConnectionError && (
          <Typography
            variant="body1"
            sx={{ mt: '36px', color: 'red', width: '100%' }}
          >
            {error}
          </Typography>
        )}
        <Stack
          component="form"
          onSubmit={handleSubmit}
          spacing={4}
          sx={{ mt: error ? '48px' : '0px', width: '100%' }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            placeholder="Email Address"
            name="email"
            autoComplete="email"
            variant="outlined"
            autoFocus
            value={formState.email}
            onChange={handleChange('email')}
            error={!!error && error.includes('email')}
            inputProps={{
              pattern: '[^\\s@]+@[^\\s@]+\\.[^\\s@]+',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                border: '1.5px solid theme.palette.border.main',
                paddingX: '8px',
                fontWeight: '600',
                height: '54px',
                marginY: '0px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.border.dark,
                  borderWidth: '1.5px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '2px',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.border.main,
                borderWidth: '1.5px',
              },
              '& .MuiOutlinedInput-root .MuiInputBase-input::placeholder': {
                color: theme.palette.info.light,
                opacity: 1,
              },
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            placeholder="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            variant="outlined"
            value={formState.password}
            onChange={handleChange('password')}
            inputProps={{
              minLength: 8,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                border: '1.5px solid theme.palette.border.main',
                paddingX: '8px',
                fontWeight: '600',
                height: '54px',
                marginY: '0px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.border.dark,
                  borderWidth: '1.5px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '2px',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.border.main,
                borderWidth: '1.5px',
              },
              '& .MuiOutlinedInput-root .MuiInputBase-input::placeholder': {
                color: theme.palette.info.light,
                opacity: 1,
              },
            }}
          />
          <Link color="primary" href="/forgot-password">
            Forgot password?
          </Link>
          <PrimaryButton
            type="submit"
            fullWidth
            disableRipple
            variant="contained"
            sx={{ height: '54px !important' }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Log In'}
          </PrimaryButton>
        </Stack>
        <Divider sx={{ width: '100%', my: 3 }}>or</Divider>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleGoogleLogin}
          startIcon={<GoogleIcon />}
          sx={{
            height: '54px',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            fontFamily: 'area-normal, sans-serif',
            borderColor: theme.palette.border.main,
            borderWidth: '1.5px',
            color: theme.palette.text.primary,
            backgroundColor: 'white',
            '&:hover': {
              borderColor: theme.palette.border.dark,
              backgroundColor: '#f8f8f8',
              borderWidth: '1.5px',
            },
          }}
        >
          Sign in with Google
        </Button>
      </Box>
    </Container>
  );
};

export default LoginForm;
